import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dayjs } from "dayjs";
import { useCustomModal } from "../../../hooks/modal/useCustomModal";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import TableOccurrenceAlertSan from "../AlertSan/TableOccurrenceAlertSan";
import { occurrenceAlertSanTableSelectFilterData } from "../data/occurrenceAlertSanTableSelectFilterData";
import { Option } from "../../../types/General/Option";
import { AuthUser } from "../../../types/User/AuthUser";
import { FilterConfig } from "../../../types/Components/Table/FilterConfig";
import { useFilter } from "../../../hooks/filter/useFilter";
import { getFiltersArray } from "../../../helpers/getFiltersArray";
import { updateSelectedFilterItemsName } from "../../../helpers/updateSelectedFilterItemsName";
import useFetchData from "../../../hooks/data/useFetchData";
import { getFieldOccurencesTypes } from "../../../services/FieldOccurences/getFieldOccurencesTypes";
import { getFieldOccurencesLevels } from "../../../services/FieldOccurences/getFieldOccurencesLevels";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import { getAlertSeverities } from "../../../services/FieldOccurences/getAlertSeverities";
import { getDataPeriod } from "../../../services/Indicator/getDataPeriod";

interface UseOccurrenceAlertSanProps {
  user: AuthUser | null | undefined;
  receivedFilters?: any;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  occurrenceAlertSanDataTable: any[];
  originalOccurrenceAlertSanDataTable: any[];
  setOccurrenceAlertSanDataTable: Dispatch<SetStateAction<any[]>>;
  isLoadedOccurrenceAlertSan: boolean;
  occurrenceAlertSanPageSize: number;
  setOccurrenceAlertSanPageSize: Dispatch<SetStateAction<number>>;
  occurrenceAlertSanTotalItems: number;
}

interface FilterState {
  TypeId: Option | null;
  LevelId: Option | null;
  DataPeriodId: Option | null;
  CreatedAtStart: Dayjs | null;
  CreatedAtEnd: Dayjs | null;
  IsActive: Option | null;
  ProvinceId: Option | null;
  CountyId: Option | null;
  AlertSeverityId: Option | null;
}

interface OptionsState {
  typeOptions: Option[];
  levelOptions: Option[];
  dataPeriodOptions: Option[];
  createdAtStartOptions: Dayjs[];
  createdAtEndOptions: Dayjs[];
  stateOptions: Option[];
  provinceOptions: Option[];
  countyOptions: Option[];
  alertSeverityOptions: Option[];
}

export const useOccurrenceAlertSan = ({
  user,
  receivedFilters,
  setIsSearching,
  occurrenceAlertSanDataTable,
  originalOccurrenceAlertSanDataTable,
  setOccurrenceAlertSanDataTable,
  isLoadedOccurrenceAlertSan,
  occurrenceAlertSanPageSize,
  setOccurrenceAlertSanPageSize,
  occurrenceAlertSanTotalItems,
}: UseOccurrenceAlertSanProps) => {
  const [
    selectedFilterItemsOccurrenceAlertSan,
    setSelectedFilterItemsOccurrenceAlertSan,
  ] = useState<string>("");

  const [filterState, setFilterState] = useState<FilterState>({
    TypeId: null,
    LevelId: null,
    DataPeriodId: null,
    CreatedAtStart: null,
    CreatedAtEnd: null,
    IsActive: null,
    ProvinceId:
      receivedFilters?.province ??
      (user?.role?.level?.id !== undefined &&
      user?.role?.level?.id >= 4 &&
      user?.province
        ? { id: user.province.id, name: user.province.name }
        : null),
    CountyId:
      receivedFilters?.county ??
      (user?.role?.level?.id !== undefined &&
      user?.role?.level?.id === 5 &&
      user?.county
        ? { id: user.county.id, name: user?.county.name }
        : null),
    AlertSeverityId: null,
  });

  const [optionsState, setOptionsState] = useState<OptionsState>({
    typeOptions: [] as Option[],
    levelOptions: [] as Option[],
    dataPeriodOptions: [] as Option[],
    createdAtStartOptions: [] as Dayjs[],
    createdAtEndOptions: [] as Dayjs[],
    stateOptions: [
      { id: "true", name: "A decorrer" },
      { id: "false", name: "Terminado" },
    ],
    provinceOptions: [] as Option[],
    countyOptions: [] as Option[],
    alertSeverityOptions: [] as Option[],
  });

  const {
    control: controlOccurrenceAlertSan,
    reset: resetOccurrenceAlertSan,
    setValue: setValueOccurrenceAlertSan,
  } = useForm();
  const filterNames = useMemo(
    () => [
      "TypeId",
      "IsActive",
      "DataPeriodId",
      "CreatedAtStart",
      "CreatedAtEnd",
      "StateId",
      "ProvinceId",
      "CountyId",
      "AlertSeverityId",
      "Title",
    ],
    [],
  );
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);
  const {
    filters: occurrenceAlertSanFilters,
    setFilter: setOccurrenceAlertSanFilter,
  } = useFilter({ setValue: setValueOccurrenceAlertSan, filterNames });

  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );

  const { isLoaded: levelLoaded } = useFetchData(
    getFieldOccurencesLevels,
    (data) => setOptionsState((prev) => ({ ...prev, levelOptions: data })),
  );

  const { isLoaded: typeLoaded } = useFetchData(
    getFieldOccurencesTypes,
    (data) => setOptionsState((prev) => ({ ...prev, typeOptions: data })),
  );

  const { isLoaded: alertSeverityLoaded } = useFetchData(
    getAlertSeverities,
    (data) =>
      setOptionsState((prev) => ({ ...prev, alertSeverityOptions: data })),
  );

  const { isLoaded: dataPeriodLoaded } = useFetchData(getDataPeriod, (data) =>
    setOptionsState((prev) => ({ ...prev, dataPeriodOptions: data })),
  );

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (filterState.ProvinceId) {
        getCountyById({ ParentId: filterState.ProvinceId.id })
          .then((result) => {
            setOptionsState((prev) => ({
              ...prev,
              countyOptions: result,
            }));
          })
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyLoaded(true);
        setOptionsState((prev) => ({ ...prev, countyOptions: [] }));
      }
    };
    updateCountyOptions();
  }, [filterState.ProvinceId]);

  useEffect(() => {
    const displayFormats = {
      CreatedAtStart: "DD/MM/YYYY",
      CreatedAtEnd: "DD/MM/YYYY",
    };
    updateSelectedFilterItemsName(
      filterState,
      occurrenceAlertSanFilters,
      setSelectedFilterItemsOccurrenceAlertSan,
      filterNames,
      displayFormats,
    );
  }, [occurrenceAlertSanFilters, filterState, filterNames]);

  const filtersArray = getFiltersArray(
    filterNames,
    occurrenceAlertSanFilters,
    setOccurrenceAlertSanFilter,
  );

  const filterChildren = occurrenceAlertSanTableSelectFilterData(
    optionsState.typeOptions,
    optionsState.levelOptions,
    optionsState.dataPeriodOptions,
    optionsState.stateOptions,
    optionsState.provinceOptions,
    optionsState.countyOptions,
    optionsState.alertSeverityOptions,
    controlOccurrenceAlertSan,
    setOccurrenceAlertSanFilter,
    (CreatedAtStart: any) =>
      setFilterState((prev) => ({ ...prev, CreatedAtStart })),
    (CreatedAtEnd: any) =>
      setFilterState((prev) => ({ ...prev, CreatedAtEnd })),
    (TypeId: any) => setFilterState((prev) => ({ ...prev, TypeId })),
    (LevelId: any) => setFilterState((prev) => ({ ...prev, LevelId })),
    (DataPeriodId: any) =>
      setFilterState((prev) => ({ ...prev, DataPeriodId })),
    (IsActive: any) => setFilterState((prev) => ({ ...prev, IsActive })),
    (ProvinceId: any) => setFilterState((prev) => ({ ...prev, ProvinceId })),
    (CountyId: any) => setFilterState((prev) => ({ ...prev, CountyId })),
    (AlertSeverityId: any) =>
      setFilterState((prev) => ({ ...prev, AlertSeverityId })),
    filterState.ProvinceId,
    filterState.CreatedAtStart,
    filterState.CreatedAtEnd,
    alertSeverityLoaded,
    provinceLoaded,
    countyLoaded,
    typeLoaded,
    levelLoaded,
    dataPeriodLoaded,
    user,
    receivedFilters,
  );

  const {
    isOpen: isOpenOccurrenceAlertSan,
    title: titleOccurrenceAlertSan,
    buttonTitle: buttonTitleOccurrenceAlertSan,
    openModal: openModalOccurrenceAlertSan,
    closeModal: closeModalOccurrenceAlertSan,
  } = useCustomModal("", "");

  const tableConfigOccurrenceAlertSan: TableConfig = {
    tableName: "Lista de Ocorrências de Alerta SAN",
    searchTitle: "Pesquisar pelo Título da ocorrência de Alerta SAN",
    dataTable: occurrenceAlertSanDataTable,
    originalDataTable: originalOccurrenceAlertSanDataTable,
    setDataTable: setOccurrenceAlertSanDataTable,
    renderTable: (occurrenceAlertSanDataTable) => (
      <TableOccurrenceAlertSan
        dataTable={occurrenceAlertSanDataTable}
        isLoaded={isLoadedOccurrenceAlertSan}
        setValue={setValueOccurrenceAlertSan}
        openModal={openModalOccurrenceAlertSan}
      />
    ),
    isLoaded: isLoadedOccurrenceAlertSan,
  };

  const {
    currentPage: occurrenceAlertSanCurrentPage,
    totalPages: occurrenceAlertSanTotalPages,
    handlePageChange: occurrenceAlertSanHandlePageChange,
    renderContent: occurrenceAlertSanRenderContent,
  } = useTableRendering(
    tableConfigOccurrenceAlertSan,
    occurrenceAlertSanPageSize,
    occurrenceAlertSanTotalItems,
    setIsSearching,
  );

  const paginationConfigOccurrenceAlertSan: PaginationConfig = {
    pageSize: occurrenceAlertSanPageSize,
    currentPage: occurrenceAlertSanCurrentPage,
    totalPages: occurrenceAlertSanTotalPages,
    handlePageChange: occurrenceAlertSanHandlePageChange,
    renderContent: occurrenceAlertSanRenderContent,
    setPageSize: setOccurrenceAlertSanPageSize,
    totalItems: occurrenceAlertSanTotalItems,
  };

  const modalConfigOccurrenceAlertSan: ModalConfig = {
    isOpen: isOpenOccurrenceAlertSan,
    title: titleOccurrenceAlertSan,
    buttonTitle: buttonTitleOccurrenceAlertSan,
    openModal: openModalOccurrenceAlertSan,
    closeModal: closeModalOccurrenceAlertSan,
  };

  const filterConfigOccurrenceAlertSan: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItemsOccurrenceAlertSan,
  };

  return {
    controlOccurrenceAlertSan,
    paginationConfigOccurrenceAlertSan,
    modalConfigOccurrenceAlertSan,
    tableConfigOccurrenceAlertSan,
    occurrenceAlertSanCurrentPage,
    filterConfigOccurrenceAlertSan,
    occurrenceAlertSanFilters,
    setOccurrenceAlertSanFilter,
    resetOccurrenceAlertSan,
    setOccurrenceAlertSanCreatedAtStart: (CreatedAtStart: any) =>
      setFilterState((prev) => ({ ...prev, CreatedAtStart })),
    setOccurrenceAlertSanCreatedAtEnd: (CreatedAtEnd: any) =>
      setFilterState((prev) => ({ ...prev, CreatedAtEnd })),
  };
};
